import { useEffect, useRef, useState } from 'react';
import { useFileStore } from '@/lib/file-store';
import { useWebContainerStore } from '@/lib/webcontainer-store';

type FileLoaderHookParams = {
  siteId: string;
  themeId: string;
  themeBuildId: string;
  enabled?: boolean;
  onLoadComplete?: () => void;
};

export function useLoadFiles({ siteId, themeId, themeBuildId, enabled = true, onLoadComplete }: FileLoaderHookParams) {
  const { addFile, setFiles } = useFileStore();
  const { instance: webcontainer } = useWebContainerStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const lastBuildIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastBuildIdRef.current !== themeBuildId) {
      lastBuildIdRef.current = themeBuildId || null;
      setIsLoaded(false);
      setFiles([]);
    }

    if (!enabled || !siteId || !themeId || !themeBuildId || isLoaded) return;

    const loadFiles = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          `/sites/${siteId}/themes/${themeId}/theme_builds/${themeBuildId}/ai_code_assistants`,
          {
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          
          // Load files into the file store and WebContainer
          if (data.files && Array.isArray(data.files)) {
            for (const file of data.files) {
              if (file.path && file.content !== undefined) {
                // Add to file store with metadata (this updates the UI)
                addFile(file.path, file.content, {
                  id: file.id,
                  currentVersion: file.current_version,
                  versionCount: file.version_count
                });
                
                // Write to WebContainer if available
                if (webcontainer) {
                  try {
                    // Ensure parent directories exist
                    const pathParts = file.path.split('/');
                    if (pathParts.length > 1) {
                      const dirPath = pathParts.slice(0, -1).join('/');
                      await webcontainer.fs.mkdir(dirPath, { recursive: true });
                    }
                    await webcontainer.fs.writeFile(file.path, file.content);
                    console.log(`Wrote file to WebContainer: ${file.path}`);
                  } catch (error) {
                    console.error(`Failed to write file ${file.path} to WebContainer:`, error);
                  }
                }
              }
            }
            console.log(`Loaded ${data.files.length} files from backend to file store and WebContainer`);
          }
          
          setIsLoaded(true);
          
          // Notify that loading is complete
          if (onLoadComplete) {
            onLoadComplete();
          }
        }
      } catch (error) {
        console.error('Failed to load files:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadFiles();
  }, [siteId, themeId, themeBuildId, enabled, addFile, setFiles, webcontainer, isLoaded, onLoadComplete]);

  return { isLoading, isLoaded };
}
